import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { DeviceFactory } from '../../../device-factory/device-factory';

@Component({
	selector: 'app-end-voltage-chart',
	templateUrl: './end-voltage-chart.component.html',
	styleUrls: ['./end-voltage-chart.component.css']
})
export class EndVoltageChartComponent implements OnChanges {
	@Input() event_type = 'inuse';
	@Input() sctDeviceType;
	@Input() title;
	@Input() options: {hasBatteryOptions, hasCapacityOptions, hasTruckTypeOptions} = {hasBatteryOptions: true, hasCapacityOptions: true, hasTruckTypeOptions: false};
	totalEventsCount: number = 0;
	DeviceFactory = DeviceFactory;

	@Input() userParentFilters: boolean = false;

	maxDate = new Date((new Date()).getTime() - 24 * 60 * 60 * 1000); // yesterday
	@Input() toDate = this.maxDate; // yesterday
	@Input() fromDate = new Date((new Date()).getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
	minDate = this.fromDate;

	batteryVoltagesOptions: any[] = [];
	@Input() selectedBatteryVoltage: string = 'all';

	batteryCapacitiesOptions: any[] = [];
	@Input() selectedBatteryCapacity: string = 'all';

	@Input() selectedBatteryVintage: string[] = ['all'];
	endVoltageData = [];

	batteryManufacturersOptions: any[] = [];
	@Input() selectedBatteryManufacturer: string = 'all';

	@Output() prepareParentFiltersOptions = new EventEmitter<{ batteryVoltagesOptions: any[], batteryCapacitiesOptions: any[], batteryManufacturersOptions: any[] }>();
	@Output() chartDataReady = new EventEmitter<{ title: string, xAxisLabel: string, yAxisLabel: string, data: any[] , totalEventsCount: number}>();
	truckTypeList: any[] = [];
	selectedEndVoltageTruckTypeFilter: string = 'all'

	siteId = 0;
	chartColumns = ["VPC range", "battery %"];
	voltageChartData = [];
	chartConfig: any = {
		legend: { position: 'top' },
		bar: {
			groupWidth: 30
		},
		tooltip: {isHtml: true},
		chartArea: { width: '80%', height: '60%', bottom: 80 }, 
		animation: { startup: true, duration: 500, easing: 'out' },
	};

	steps: number[] = [];

	constructor(
		private route: ActivatedRoute,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private siteService: SitesService,
		private commonService: CommonService,
	) {}

	ngOnInit() {
		this.siteId = this.route.parent.snapshot.params.siteId || 0;

		this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
			if (siteInfo && siteInfo.installation_date) {
				this.minDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
				this.getEndVoltageChart();
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.userParentFilters) {
			if (changes?.['fromDate'])
				this.changeDate('fromDate', changes['fromDate'].currentValue);

			if (changes?.['toDate'])
				this.changeDate('toDate', changes['toDate'].currentValue);

			if (changes?.['selectedBatteryVoltage'] || changes?.['selectedBatteryCapacity'] || changes?.['selectedBatteryVintage'] || changes?.['selectedBatteryManufacturer'])
				this.getEndVoltageChart();
		}
	}

	setChartConfig() {
		if (this.sctDeviceType == DeviceFactory.types.iotah) {
			this.chartConfig.vAxis = { title: `${this.translate.instant('g.cycles')} %` };
			this.chartConfig.hAxis = { title: this.translate.instant('event.vpc') };
			this.chartColumns = ['VPC Range', `${this.translate.instant('g.cycles')} %`];
		} else {
			const ticks = [];
			for (let i = 0; i <= 100; i += 20) {
				ticks.push(i);
			}

			if (this.event_type == 'charge') {
				this.chartConfig.vAxis = { title: this.translate.instant('fleet_reports.charge_cycles_percent'), format: '#\'%\'', ticks: ticks };
				this.chartConfig.hAxis = { title: this.translate.instant('event.vpc'), ticks: this.steps };
				this.chartColumns = ["VPC range", this.translate.instant('fleet_reports.charge_cycles_percent')];
			} else {
				this.chartConfig.vAxis = { title: this.translate.instant('fleet_reports.in_use_cycles_percent'), format: '#\'%\'', ticks: ticks };
				this.chartConfig.hAxis = { title: this.translate.instant('event.vpc'), ticks: this.steps };
				this.chartColumns = ["VPC range", this.translate.instant('fleet_reports.in_use_cycles_percent')];
			}
		}

		this.chartDataReady.emit({
			title: this.event_type === 'inuse' ? this.translate.instant('fleet_reports.inuse_end_voltage') : this.translate.instant('fleet_reports.charge_end_voltage'),
			xAxisLabel: this.chartConfig.hAxis.title,
			yAxisLabel: this.chartConfig.vAxis.title,
			data: this.voltageChartData,
			totalEventsCount: this.totalEventsCount
		});
	}

	changeDate(field, value) {
		this[field] = value;

		this.getEndVoltageChart();
	}

	getEndVoltageChart() {
		if (!this.siteId)
			return;

		const zoneDiff	= new Date().getTimezoneOffset() * -1;
		let fromDate:any	= new Date(new Date(this.fromDate).getTime() + (zoneDiff * 60 * 1000));
		let toDate:any = new Date(new Date(this.toDate).getTime() + (zoneDiff * 60 * 1000));
		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		const options: any = {
			from_date: fromDate,
			to_date: toDate,
			battery_voltage: this.selectedBatteryVoltage,
			battery_capacity: this.selectedBatteryCapacity,
			battery_vintage: this.selectedBatteryVintage,
			battery_manufacturer: this.selectedBatteryManufacturer,
			truck_type: this.selectedEndVoltageTruckTypeFilter,
		}

		if (this.sctDeviceType)
			options.sct_device_type = this.sctDeviceType;

		this.siteService.getEndVoltageChart(this.siteId, this.event_type, options).subscribe((data: any) => {
			this.prepareVoltageData(data);
			this.batteryVoltagesOptions = this.commonService.prepareOptions(data.batteryVoltages);
			this.batteryCapacitiesOptions = this.commonService.prepareOptions(data.batteryCapacities);
			this.batteryManufacturersOptions = this.commonService.prepareOptions(data.batteryManufacturers);

			this.prepareParentFiltersOptions.emit({
				batteryVoltagesOptions: this.batteryVoltagesOptions,
				batteryCapacitiesOptions: this.batteryCapacitiesOptions,
				batteryManufacturersOptions: this.batteryManufacturersOptions,
			});

			this.truckTypeList = this.commonService.prepareOptions(data.truckTypes);
			this.steps = data.chartSteps || [];
			this.setChartConfig();
		});
	}

	prepareVoltageData(data: any) {
		const chartData = [];
		let index = 0;
		for (const step of data.chartSteps || []) {
			const stepPercent = data.totalEventsCount ? (data.chartData[index] / data.totalEventsCount) * 100 : 0;
			chartData.push([step, +(stepPercent).toFixed(2)]);
			index++;
		}
		this.totalEventsCount = data.totalEventsCount;
		this.voltageChartData = chartData;
	}
}
